import React, { useState, useCallback } from 'react';
import { Box, Stepper, Step, StepLabel, Grid2 } from '@mui/material';
import NewPackage from './NewPackage';
import PackageDefinition from './PackageDefinition';
import { useSearchParams } from 'react-router-dom';
import { PackageDetailsValues, getPackageDetailsURL } from './PackageConfigHelper';
import { useAppQuery } from '../../../services/appQuery';
import { getRequest } from '../../../services/httpService';
import { useTranslation } from 'react-i18next';

const PackageConfigDetails: React.FC = () => {
  const { t: tBilling } = useTranslation('billing_config');
  const PACKAGE_STEPS = [
    tBilling('package_config.package_details', { defaultValue: 'Package Details' }),
    tBilling('package_config.package_definition', { defaultValue: 'Package Definition' }),
  ];
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [packageData, setPackageData] = useState<PackageDetailsValues | null>(null);
  const [packageId, setPackageId] = useState<number | null>(null);
  const [isStep2Enabled, setIsStep2Enabled] = useState(false);
  const [hasAutoNavigated, setHasAutoNavigated] = useState(false);
  const [searchParams] = useSearchParams();
  const packageIdParam = searchParams.get('packageId');
  const parsedPackageId = packageIdParam ? Number(packageIdParam) : undefined;

  // Fetch package data - this will be used by PackageDefinition and can be refetched
  const { data: fetchedPackageData, refetch: refetchPackageData } = useAppQuery<PackageDetailsValues>(
    ['PackageDetailsConfig', packageId],
    () => getRequest<PackageDetailsValues>(`${getPackageDetailsURL()}${packageId}/`),
    {
      enabled: typeof packageId === 'number',
      staleTime: 0,
      gcTime: 0,
    }
  );

  // Update packageData when fetchedPackageData changes
  React.useEffect(() => {
    if (fetchedPackageData) {
      setPackageData(fetchedPackageData);
    }
  }, [fetchedPackageData]);

  // Refetch handler to pass to PackageDefinition
  const handleRefetchPackageData = useCallback(async () => {
    await refetchPackageData();
  }, [refetchPackageData]);

  // If editing existing package, start from step 0
  React.useEffect(() => {
    if (parsedPackageId) {
      setActiveStep(0);
      // Mark step 1 as completed when loading existing package
      const newCompleted = new Set<number>();
      newCompleted.add(0);
      setCompletedSteps(newCompleted);
      setPackageId(parsedPackageId);
    }
  }, [parsedPackageId]);

  const handleStep1Complete = (packageDetails: PackageDetailsValues) => {
    // Save package data and ID from the save response
    setPackageData(packageDetails);
    setPackageId((packageDetails as unknown as { id?: number }).id ?? null);
    
    // Mark step 1 as completed
    const newCompleted = new Set(completedSteps);
    newCompleted.add(0);
    setCompletedSteps(newCompleted);
    
    // Move to step 2
    setActiveStep(1);
  };

  const handleBackToStep1 = () => {
    setActiveStep(0);
  };

  const handleStep1ValidationChange = (isValid: boolean) => {
    // Mark step 1 as completed when form is valid (for edit/view mode)
    const newCompleted = new Set(completedSteps);
    if (isValid) {
      newCompleted.add(0);
    } else {
      newCompleted.delete(0);
    }
    setCompletedSteps(newCompleted);
  };

  const handleTariffsAvailable = useCallback((hasTariffs: boolean, existingPackageData: PackageDetailsValues) => {
    if (hasTariffs) {
      setIsStep2Enabled(true);
      setPackageData(existingPackageData);
      
      // Mark step 2 as completed if package is finalized
      if (existingPackageData?.freeze) {
        setCompletedSteps(prev => {
          const newCompleted = new Set(prev);
          newCompleted.add(0);
          newCompleted.add(1);
          return newCompleted;
        });
      }
      
      // Automatically go to step 2 only on first load (not after manual navigation)
      // and only if package is not finalized (freeze)
      if (!hasAutoNavigated && !existingPackageData?.freeze) {
        setHasAutoNavigated(true);
        setActiveStep(1);
      }
    }
  }, [hasAutoNavigated]);

  return (
    <Box sx={{ p: 3}}>
      <Grid2 mb={2} container>
        <Grid2 size={4}>
          {/* Stepper */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              padding: 2,
              backgroundColor: '#fff',
              borderRadius: 3,
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',             
            }}
          >
            <Stepper
              alternativeLabel
              activeStep={activeStep}
              sx={{
                width: '100%',
                '& .MuiStepConnector-line': {
                  borderTopWidth: 1.5,
                  borderColor: '#E0E0E0',
                },
              }}
            >
              {PACKAGE_STEPS.map((label, index) => {
                const isStepEnabled = index === 0 || (index === 1 && completedSteps.has(0) && isStep2Enabled);
                const isCompleted = completedSteps.has(index);
                const isActive = index === activeStep;
                
                return (
                  <Step key={label} completed={isCompleted}>
                    <StepLabel
                      onClick={() => {
                      // Allow clicking on step 2 only if step 1 is completed and step 2 is enabled
                        if (index === 1 && completedSteps.has(0) && isStep2Enabled) {
                          setActiveStep(index);
                        }
                        // Allow clicking on step 1 anytime
                        if (index === 0) {
                          setActiveStep(index);
                        }
                      }}
                      sx={{
                        cursor: isStepEnabled ? 'pointer' : 'no-drop',
                        opacity: isStepEnabled ? 1 : 0.5,
                        '& .MuiStepIcon-root': {
                          color: isCompleted 
                            ? '#016A1C' 
                            : isActive 
                              ? '#E17E61' 
                              : isStepEnabled 
                                ? '#5A9BD5'
                                : '#BDBDBD',
                          backgroundColor: 'transparent',
                        },
                        '& .MuiStepIcon-root.Mui-completed': {
                          color: '#016A1C',
                        },
                        '& .MuiStepIcon-root.Mui-active': {
                          color: isCompleted ? '#016A1C' : '#E17E61',
                        },
                        '& .MuiStepIcon-text': {
                          fill: '#FFF',
                        },
                        '& .MuiStepLabel-label': {
                          fontFamily: 'Montserrat',
                          fontSize: '14px',
                          fontWeight: 600,
                          color: isCompleted
                            ? '#016A1C'
                            : isActive
                              ? '#E17E61'
                              : isStepEnabled
                                ? '#5A9BD5'
                                : '#BDBDBD',
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                );})}
            </Stepper>
          </Box>
        </Grid2>
      </Grid2>


      {/* Step Content */}
      <Box>
        {activeStep === 0 && (
          <NewPackage 
            onStepComplete={handleStep1Complete} 
            packageIdFromStepper={packageId}
            onValidationChange={handleStep1ValidationChange}
            onTariffsAvailable={handleTariffsAvailable}
          />
        )}
        
        {activeStep === 1 && (packageData || isStep2Enabled) && (
          <PackageDefinition
            onBackToStep1={handleBackToStep1}
            packageId={packageId}
            initialPackageData={packageData ? {
              ...packageData,
              tariffs: packageData.tariffs ?? [],
            } : undefined}
            onRefetchPackageData={handleRefetchPackageData}
          />
        )}
      </Box>
    </Box>
  );
};

export default PackageConfigDetails;
